/**
 * Axios client + auth glue.
 *
 *  - default `api` is the public client (no auth)
 *  - `apiAuth` is the admin client. It:
 *     · injects `Authorization: Bearer <token>` from in-memory accessToken
 *     · sends cookies (withCredentials) so the httpOnly `wit_refresh`
 *       cookie reaches /api/auth/refresh
 *     · transparently refreshes on a 401 and retries the original request once
 *     · on refresh failure, fires a window event so AuthContext can sign-out
 */
import axios from "axios";
import silentRefresh from "@/lib/silentRefresh";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---- public client (existing usage across non-admin pages) ----
const api = axios.create({
  baseURL: API,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

export default api;

// ---- admin client ----
let _accessToken = null;
const REFRESH_FAILED_EVENT = "wit:auth-refresh-failed";

export function setAccessToken(token) {
  _accessToken = token || null;
}

export function getAccessToken() {
  return _accessToken;
}

export const apiAuth = axios.create({
  baseURL: API,
  timeout: 20000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

apiAuth.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

let _refreshing = null;

apiAuth.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._isRetry &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/login")
    ) {
      try {
        if (!_refreshing) {
          _refreshing = silentRefresh(`${API}/auth/refresh`).finally(() => {
            setTimeout(() => { _refreshing = null; }, 0);
          });
        }
        const data = await _refreshing;
        if (!data?.access_token) throw new Error("refresh failed");
        setAccessToken(data.access_token);
        original._isRetry = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return apiAuth(original);
      } catch (_e) {
        setAccessToken(null);
        window.dispatchEvent(new Event(REFRESH_FAILED_EVENT));
      }
    }
    return Promise.reject(error);
  }
);

export const REFRESH_FAILED = REFRESH_FAILED_EVENT;
